/**
 * WebSocket Context for React components
 * Provides hooks for real-time updates
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import websocketService from './websocket';
import { getToken } from './tokenStorage';

// Context
const WebSocketContext = createContext(null);

/**
 * WebSocket Provider Component
 */
export function WebSocketProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  useEffect(() => {
    // Get token from localStorage (survives iPad tab-suspend/multi-tab, unlike sessionStorage)
    const token = getToken();

    if (token) {
      // Subscribe to connection events
      const unsubConnect = websocketService.subscribe('connection', ({ status }) => {
        setConnectionStatus(status);
        setIsConnected(status === 'connected');
      });

      // Connect
      websocketService.connect(token);

      return () => {
        unsubConnect();
        websocketService.disconnect();
      };
    }
  }, []);

  const value = {
    isConnected,
    connectionStatus,
    subscribe: websocketService.subscribe.bind(websocketService),
    send: websocketService.send.bind(websocketService),
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to access WebSocket context
 */
export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

/**
 * Hook to subscribe to build logs
 * @param {string} buildName - Build name to subscribe to
 * @returns {object} { logs, isStreaming, error }
 */
export function useBuildLogs(buildName) {
  const [logs, setLogs] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const { subscribe, send, isConnected } = useWebSocket();
  const logsRef = useRef([]);

  useEffect(() => {
    if (!buildName || !isConnected) return;

    // Clear previous logs
    setLogs([]);
    logsRef.current = [];
    setIsStreaming(true);
    setError(null);

    // Subscribe to build log events
    const unsubLogs = subscribe('BUILD_LOG', (data) => {
      if (data.buildName === buildName) {
        const newLog = {
          timestamp: new Date().toISOString(),
          line: data.line,
          step: data.step
        };
        logsRef.current = [...logsRef.current, newLog];
        setLogs([...logsRef.current]);
      }
    });

    // Subscribe to build complete events
    const unsubComplete = subscribe('BUILD_COMPLETE', (data) => {
      if (data.buildName === buildName) {
        setIsStreaming(false);
      }
    });

    // Subscribe to build error events
    const unsubError = subscribe('BUILD_ERROR', (data) => {
      if (data.buildName === buildName) {
        setError(data.error);
        setIsStreaming(false);
      }
    });

    // Request log stream
    send({
      type: 'SUBSCRIBE_BUILD_LOGS',
      buildName
    });

    return () => {
      unsubLogs();
      unsubComplete();
      unsubError();
      send({
        type: 'UNSUBSCRIBE_BUILD_LOGS',
        buildName
      });
    };
  }, [buildName, isConnected, subscribe, send]);

  const clearLogs = useCallback(() => {
    setLogs([]);
    logsRef.current = [];
  }, []);

  return { logs, isStreaming, error, clearLogs };
}

/**
 * Hook to subscribe to build status updates
 * @param {string} buildName - Build name to watch
 * @returns {object} { status, steps, error }
 */
export function useBuildStatus(buildName) {
  const [status, setStatus] = useState(null);
  const [steps, setSteps] = useState([]);
  const [error, setError] = useState(null);
  const { subscribe, isConnected } = useWebSocket();

  useEffect(() => {
    if (!buildName || !isConnected) return;

    const unsubStatus = subscribe('BUILD_STATUS', (data) => {
      if (data.buildName === buildName) {
        setStatus(data.status);
        if (data.steps) {
          setSteps(data.steps);
        }
        if (data.error) {
          setError(data.error);
        }
      }
    });

    return () => {
      unsubStatus();
    };
  }, [buildName, isConnected, subscribe]);

  return { status, steps, error };
}

/**
 * Hook to subscribe to service status updates
 * @param {string} serviceName - Service name to watch
 * @returns {object} { status, metrics, error }
 */
export function useServiceStatus(serviceName) {
  const [status, setStatus] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  const { subscribe, isConnected } = useWebSocket();

  useEffect(() => {
    if (!serviceName || !isConnected) return;

    const unsubStatus = subscribe('SERVICE_STATUS', (data) => {
      if (data.serviceName === serviceName) {
        setStatus(data.status);
        if (data.metrics) {
          setMetrics(data.metrics);
        }
        if (data.error) {
          setError(data.error);
        }
      }
    });

    return () => {
      unsubStatus();
    };
  }, [serviceName, isConnected, subscribe]);

  return { status, metrics, error };
}

/**
 * Hook to subscribe to notifications
 * @returns {object} { notifications, unreadCount, markAsRead }
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const { subscribe, isConnected } = useWebSocket();

  useEffect(() => {
    if (!isConnected) return;

    const unsub = subscribe('NOTIFICATION', (data) => {
      setNotifications(prev => [data, ...prev].slice(0, 50)); // Keep last 50
    });

    return () => {
      unsub();
    };
  }, [isConnected, subscribe]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = useCallback((ids) => {
    setNotifications(prev =>
      prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n)
    );
  }, []);

  return { notifications, unreadCount, markAsRead };
}

export default WebSocketContext;
